// Central catalog of all Kestrel integrations used by the setup wizard,
// onboarding checklist, and agent prompt generator. Each entry carries the
// metadata needed to render the picker (category, icon), drive the browser
// stepper (href), and generate CLI/agent connect instructions (auth fields).

export type IntegrationCategory =
  | 'Infrastructure'
  | 'PaaS'
  | 'AI Compute'
  | 'Observability'
  | 'On-call'
  | 'Project Management'
  | 'Databases'
  | 'Secrets'
  | 'CI/CD'
  | 'IaC & GitOps'
  | 'Security';

// How the integration is connected:
// - "token": pure API credential connect (CLI-friendly: kestrel integrations connect <key> --...)
// - "oauth": requires a browser OAuth/app-install redirect (UI only; CLI prints the URL)
// - "cluster": requires installing the Kestrel operator via Helm (CLI can mint the token)
// - "cloud": multi-step cloud IAM flow (CLI supported via bootstrap/verify)
export type IntegrationConnectionKind = 'token' | 'oauth' | 'cluster' | 'cloud';

export interface IntegrationCredentialField {
  // JSON body field name expected by the connect endpoint / CLI flag name (snake_case)
  name: string;
  label: string;
  required: boolean;
  secret: boolean;
  placeholder?: string;
  help?: string;
}

export interface IntegrationDef {
  key: string; // stable id, matches /api/workflows/integrations/status ids where possible
  name: string;
  category: IntegrationCategory;
  description: string;
  href: string; // in-app settings page
  connectionKind: IntegrationConnectionKind;
  // For token integrations: the fields the connect endpoint accepts.
  fields?: IntegrationCredentialField[];
  // CLI connect command template shown in the agent prompt / CLI instructions.
  cliCommand?: string;
  // id used by GET /api/workflows/integrations/status (defaults to key)
  statusId?: string;
  docsNote?: string;
  popular?: boolean;
  // Example plain-English workflow prompt featuring this integration, shown on
  // the setup wizard's final step when the user connected it.
  workflowExample?: string;
}

export const INTEGRATION_CATALOG: IntegrationDef[] = [
  // --- Infrastructure ---
  {
    key: 'kubernetes',
    name: 'Kubernetes',
    category: 'Infrastructure',
    description: 'Onboard clusters to monitor workloads, network traffic, and incidents via the Kestrel operator.',
    href: '/onboard-cluster',
    connectionKind: 'cluster',
    cliCommand: 'kestrel integrations connect kubernetes --cluster-name <name>',
    docsNote: 'Generates an operator token and Helm install command; run the printed helm install against your cluster.',
    popular: true,
    workflowExample: 'When a pod is crash-looping in production, investigate the cause and post a summary to Slack.',
  },
  {
    key: 'aws',
    name: 'AWS',
    category: 'Infrastructure',
    description: 'Connect AWS accounts via IAM role to unlock the AWS cost, compute, security, and data workflow blocks.',
    href: '/cloud-integrations',
    connectionKind: 'cloud',
    statusId: 'aws-cost',
    cliCommand: 'kestrel integrations connect aws --role-arn <arn> [--external-id <id>] [--region us-east-1]',
    docsNote: 'Two-step flow: `kestrel integrations connect aws --bootstrap` prints the CloudFormation/IAM setup, then verify with --role-arn.',
    popular: true,
    workflowExample: 'When AWS costs spike above my daily budget, find the responsible services and alert me.',
  },
  {
    key: 'gcp',
    name: 'Google Cloud',
    category: 'Infrastructure',
    description: 'Connect GCP projects with keyless Workload Identity Federation for cost automation, cleanup, and more.',
    href: '/cloud-integrations',
    connectionKind: 'cloud',
    statusId: 'gcp-cost',
    cliCommand: 'kestrel integrations connect gcp --project-id <project> [--billing-export-table <project.dataset.table>]',
    docsNote: 'Two-step flow: `kestrel integrations connect gcp --bootstrap --project-id <project>` prints a Cloud Shell setup script, then verify with --project-id. No service-account keys are created.',
    workflowExample: 'When my GCP spend spikes above the weekly average, find the responsible services and alert me in Slack.',
  },
  {
    key: 'karpenter',
    name: 'Karpenter',
    category: 'Infrastructure',
    description: 'Manage Karpenter NodePools and NodeClaims for Kubernetes node autoscaling.',
    href: '/integrations/karpenter',
    connectionKind: 'cluster',
    cliCommand: 'kestrel integrations connect kubernetes --cluster-name <name>',
    docsNote: 'Auto-detected: install the Kestrel operator in a cluster running Karpenter and it connects automatically.',
    workflowExample: 'When a developer asks to scale a Karpenter NodePool, raise its CPU limit and notify the platform team in Slack.',
  },
  {
    key: 'oci',
    name: 'Oracle Cloud (OCI)',
    category: 'Infrastructure',
    description: 'Connect OCI tenancies with API-key auth for resource discovery and inventory.',
    href: '/cloud-integrations',
    connectionKind: 'cloud',
    cliCommand: 'kestrel integrations connect oci --tenancy-ocid <ocid> --user-ocid <ocid> --fingerprint <fp> --private-key-file <path> --region <region>',
    fields: [
      { name: 'tenancy_ocid', label: 'Tenancy OCID', required: true, secret: false },
      { name: 'user_ocid', label: 'User OCID', required: true, secret: false },
      { name: 'fingerprint', label: 'API Key Fingerprint', required: true, secret: false },
      { name: 'private_key', label: 'Private Key (PEM)', required: true, secret: true },
      { name: 'region', label: 'Region', required: true, secret: false, placeholder: 'us-ashburn-1' },
    ],
    workflowExample: 'When an OCI compute instance goes down, investigate and notify the on-call channel.',
  },
  {
    key: 'cloudflare',
    name: 'Cloudflare',
    category: 'Infrastructure',
    description: 'Monitor zones, Workers, DNS, WAF, and tunnels; power Cloudflare workflow blocks.',
    href: '/integrations/cloudflare',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect cloudflare --api-token <token> --account-id <id>',
    fields: [
      { name: 'api_token', label: 'API Token', required: true, secret: true },
      { name: 'account_id', label: 'Account ID', required: true, secret: false },
    ],
    workflowExample: 'When a Cloudflare Worker error rate spikes, investigate and roll back the latest deployment.',
  },
  {
    key: 'nebius',
    name: 'Nebius',
    category: 'AI Compute',
    description: 'Monitor Nebius AI Cloud resources and jobs.',
    href: '/integrations/nebius',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect nebius --credentials-file <authorized-key.json> [--region <region>]',
    fields: [
      { name: 'credentials', label: 'Authorized Key JSON', required: true, secret: true, help: 'Paste the service account authorized-key JSON document' },
      { name: 'region', label: 'Region', required: false, secret: false },
    ],
    workflowExample: 'When a Nebius GPU job fails, collect the logs and post a diagnosis to Slack.',
  },
  // --- CI/CD ---
  {
    key: 'github',
    name: 'GitHub',
    category: 'CI/CD',
    description: 'Enable automated pull requests, repo analysis, and CI/CD-triggered workflows.',
    href: '/cicd-integration',
    connectionKind: 'oauth',
    cliCommand: 'kestrel integrations connect github',
    docsNote: 'GitHub App install — the CLI prints the installation URL to open in a browser.',
    popular: true,
    workflowExample: 'When a GitHub Actions workflow fails on main, investigate the failure and open an issue with the findings.',
  },
  {
    key: 'gitlab',
    name: 'GitLab',
    category: 'CI/CD',
    description: 'Enable merge request automation and GitLab pipeline-triggered workflows.',
    href: '/cicd-integration',
    connectionKind: 'oauth',
    cliCommand: 'kestrel integrations connect gitlab',
    docsNote: 'OAuth flow — the CLI prints the authorization URL to open in a browser.',
    workflowExample: 'When a GitLab pipeline fails on the default branch, run an RCA and comment on the merge request.',
  },
  {
    key: 'argocd',
    name: 'Argo CD',
    category: 'CI/CD',
    description: 'Track Argo CD application sync status and trigger workflows on degradation.',
    href: '/integrations/argocd',
    connectionKind: 'cluster',
    cliCommand: 'kestrel integrations connect kubernetes --cluster-name <name>',
    docsNote: 'Auto-detected: install the Kestrel operator in a cluster running Argo CD and it connects automatically.',
    workflowExample: 'When an Argo CD application becomes degraded, investigate the sync failure and notify the team.',
  },
  {
    key: 'jenkins',
    name: 'Jenkins',
    category: 'CI/CD',
    description: 'Trigger workflows from Jenkins builds and query job status.',
    href: '/integrations/jenkins',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect jenkins --base-url <url> --username <user> --api-token <token>',
    fields: [
      { name: 'base_url', label: 'Jenkins URL', required: true, secret: false, placeholder: 'https://jenkins.example.com' },
      { name: 'username', label: 'Username', required: true, secret: false },
      { name: 'api_token', label: 'API Token', required: true, secret: true },
    ],
    workflowExample: 'When a Jenkins build fails, pull the console log, summarize the error, and alert the channel.',
  },
  {
    key: 'circleci',
    name: 'CircleCI',
    category: 'CI/CD',
    description: 'React to CircleCI workflow and job events with Kestrel workflows.',
    href: '/integrations/circleci',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect circleci --api-token <token> [--org-slug <slug>]',
    fields: [
      { name: 'api_token', label: 'Personal API Token', required: true, secret: true },
      { name: 'org_slug', label: 'Organization Slug', required: false, secret: false, placeholder: 'gh/my-org' },
    ],
    workflowExample: 'When a CircleCI workflow fails, analyze the failed job and rerun it if the failure looks flaky.',
  },
  // --- Security ---
  {
    key: 'sonarcloud',
    name: 'SonarCloud',
    category: 'Security',
    description: 'React to SonarCloud quality gate results and query issues, hotspots, and metrics.',
    href: '/integrations/sonarcloud',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect sonarcloud --organization <org-key> --api-token <token>',
    fields: [
      { name: 'organization', label: 'Organization Key', required: true, secret: false, placeholder: 'my-org' },
      { name: 'api_token', label: 'API Token', required: true, secret: true },
    ],
    workflowExample: 'When the quality gate fails on main, list the new critical vulnerabilities and alert the security channel.',
  },
  {
    key: 'okta',
    name: 'Okta',
    category: 'Security',
    description: 'React to Okta identity events and automate user and group responses.',
    href: '/integrations/okta',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect okta --org-url <https://acme.okta.com> --api-token <token>',
    fields: [
      { name: 'org_url', label: 'Org URL', required: true, secret: false, placeholder: 'https://acme.okta.com' },
      { name: 'api_token', label: 'API Token (SSWS)', required: true, secret: true },
    ],
    workflowExample: 'When a user reports suspicious activity, clear their sessions, expire their password, and open a PagerDuty incident.',
  },
  {
    key: 'kyverno',
    name: 'Kyverno',
    category: 'Security',
    description: 'React to policy violations and manage Kyverno guardrails on your clusters.',
    href: '/integrations/kyverno',
    connectionKind: 'cluster',
    cliCommand: 'kestrel integrations connect kubernetes --cluster-name <name>',
    docsNote: 'Auto-detected: install the Kestrel operator in a cluster running Kyverno and it connects automatically.',
    workflowExample: 'When Kyverno reports a new policy violation in production, notify the platform security channel with the policy, rule, and violating resource.',
  },
  {
    key: 'trivy',
    name: 'Trivy',
    category: 'Security',
    description: 'React to CVEs, exposed secrets, misconfigurations, and compliance failures on your clusters.',
    href: '/integrations/trivy',
    connectionKind: 'cluster',
    cliCommand: 'kestrel integrations connect kubernetes --cluster-name <name>',
    docsNote: 'Auto-detected: install the Kestrel operator in a cluster running trivy-operator (helm install trivy-operator from https://aquasecurity.github.io/helm-charts) and it connects automatically.',
    workflowExample: 'When trivy-operator finds a new critical CVE in a production image, notify the platform security channel with the image, workload, and CVE IDs.',
  },
  // --- IaC & GitOps ---
  {
    key: 'terraform',
    name: 'Terraform Cloud',
    category: 'IaC & GitOps',
    description: 'Monitor Terraform Cloud runs and trigger workflows on plan/apply events.',
    href: '/integrations/terraform',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect terraform --api-token <token> --organization <org>',
    fields: [
      { name: 'api_token', label: 'API Token', required: true, secret: true },
      { name: 'organization', label: 'Organization', required: true, secret: false },
      { name: 'base_url', label: 'Base URL', required: false, secret: false, placeholder: 'https://app.terraform.io' },
    ],
    workflowExample: 'When a Terraform Cloud run errors, investigate the plan output and post the root cause to Slack.',
  },
  {
    key: 'pulumi',
    name: 'Pulumi Cloud',
    category: 'IaC & GitOps',
    description: 'Monitor Pulumi stack updates and trigger workflows on deployment events.',
    href: '/integrations/pulumi',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect pulumi --api-token <token> --organization <org>',
    fields: [
      { name: 'api_token', label: 'Access Token', required: true, secret: true },
      { name: 'organization', label: 'Organization', required: true, secret: false },
      { name: 'base_url', label: 'Base URL', required: false, secret: false, placeholder: 'https://api.pulumi.com' },
    ],
    workflowExample: 'When a Pulumi stack update fails, run an RCA and notify the infrastructure channel.',
  },
  // --- PaaS ---
  {
    key: 'vercel',
    name: 'Vercel',
    category: 'PaaS',
    description: 'Monitor deployments and trigger workflows on failed builds or rollbacks.',
    href: '/integrations/vercel',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect vercel --api-token <token> [--team-id <id>]',
    fields: [
      { name: 'api_token', label: 'API Token', required: true, secret: true },
      { name: 'team_id', label: 'Team ID', required: false, secret: false },
    ],
    popular: true,
    workflowExample: 'When a Vercel deployment fails, run an RCA and post the summary to Slack.',
  },
  {
    key: 'railway',
    name: 'Railway',
    category: 'PaaS',
    description: 'Monitor Railway services and deployments.',
    href: '/integrations/railway',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect railway --api-token <token>',
    fields: [
      { name: 'api_token', label: 'API Token', required: true, secret: true },
    ],
    workflowExample: 'When a Railway deployment fails, fetch the build logs and post a diagnosis to Slack.',
  },
  {
    key: 'flyio',
    name: 'Fly.io',
    category: 'PaaS',
    description: 'Monitor Fly.io apps, machines, and deployment health.',
    href: '/integrations/flyio',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect flyio --api-token <token> [--org-slug <slug>]',
    fields: [
      { name: 'api_token', label: 'API Token', required: true, secret: true },
      { name: 'org_slug', label: 'Organization Slug', required: false, secret: false },
    ],
    workflowExample: 'When a Fly.io machine becomes unhealthy, restart it and report what happened.',
  },
  // --- AI Compute ---
  {
    key: 'daytona',
    name: 'Daytona',
    category: 'AI Compute',
    description: 'Monitor Daytona sandboxes and dev environments.',
    href: '/integrations/daytona',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect daytona --api-key <key> --webhook-secret <secret>',
    fields: [
      { name: 'api_key', label: 'API Key', required: true, secret: true },
      { name: 'webhook_secret', label: 'Webhook Secret', required: true, secret: true },
      { name: 'api_url', label: 'API URL', required: false, secret: false },
    ],
    workflowExample: 'When a Daytona sandbox errors, capture the state, archive it, and notify me.',
  },
  {
    key: 'beam',
    name: 'Beam',
    category: 'AI Compute',
    description: 'Monitor Beam serverless GPU workloads and deployments.',
    href: '/integrations/beam',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect beam --api-token <token>',
    fields: [
      { name: 'api_token', label: 'API Token', required: true, secret: true },
      { name: 'gateway_base_url', label: 'Gateway Base URL', required: false, secret: false },
    ],
    workflowExample: 'When a Beam deployment starts failing, investigate the errors and alert the team.',
  },
  // --- Databases ---
  {
    key: 'supabase',
    name: 'Supabase',
    category: 'Databases',
    description: 'Monitor Supabase projects, database health, and auth events.',
    href: '/integrations/supabase',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect supabase --access-token <token>',
    fields: [
      { name: 'access_token', label: 'Access Token', required: true, secret: true },
      { name: 'webhook_secret', label: 'Webhook Secret', required: false, secret: true },
    ],
    workflowExample: 'When a Supabase project becomes unhealthy or a backup fails, investigate and alert me.',
  },
  {
    key: 'planetscale',
    name: 'PlanetScale',
    category: 'Databases',
    description: 'Monitor PlanetScale branches, deploy requests, and database events.',
    href: '/integrations/planetscale',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect planetscale --token-id <id> --token <token> --organization <org>',
    fields: [
      { name: 'token_id', label: 'Service Token ID', required: true, secret: false },
      { name: 'token', label: 'Service Token', required: true, secret: true },
      { name: 'organization', label: 'Organization', required: true, secret: false },
    ],
    workflowExample: 'When a PlanetScale deploy request errors, investigate the schema change and notify the team.',
  },
  {
    key: 'neon',
    name: 'Neon',
    category: 'Databases',
    description: 'Monitor Neon Postgres projects, branches, and compute.',
    href: '/integrations/neon',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect neon --api-key <key> [--org-id <id>]',
    fields: [
      { name: 'api_key', label: 'API Key', required: true, secret: true },
      { name: 'org_id', label: 'Organization ID', required: false, secret: false, help: 'Auto-detected when the key has access to a single organization; required if it can see multiple.' },
    ],
    workflowExample: 'When a Neon branch migration fails, investigate the failure and post the findings to Slack.',
  },
  {
    key: 'clickhouse',
    name: 'ClickHouse',
    category: 'Databases',
    description: 'Monitor ClickHouse Cloud services and query performance.',
    href: '/integrations/clickhouse',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect clickhouse --key-id <id> --key-secret <secret> [--org-id <id>]',
    fields: [
      { name: 'key_id', label: 'API Key ID', required: true, secret: false },
      { name: 'key_secret', label: 'API Key Secret', required: true, secret: true },
      { name: 'org_id', label: 'Organization ID', required: false, secret: false },
    ],
    workflowExample: 'When a ClickHouse service degrades or queries slow down, investigate and alert the on-call.',
  },
  {
    key: 'databricks',
    name: 'Databricks',
    category: 'Databases',
    description: 'React to failed job runs, cluster terminations, and DLT pipeline failures from workflows.',
    href: '/integrations/databricks',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect databricks --workspace-url <https://dbc-xxx.cloud.databricks.com> --api-token <token>',
    fields: [
      { name: 'workspace_url', label: 'Workspace URL', required: true, secret: false, placeholder: 'https://dbc-a1b2c3d4-e5f6.cloud.databricks.com' },
      { name: 'api_token', label: 'Personal Access Token', required: true, secret: true },
    ],
    workflowExample: 'When a Databricks job run fails, fetch the run output, repair the failed tasks, and post the error to Slack.',
  },
  // --- Secrets ---
  {
    key: 'vault',
    name: 'HashiCorp Vault',
    category: 'Secrets',
    description: 'Manage KV secrets, policies, and leases; react to seal-status and secret lifecycle events.',
    href: '/integrations/vault',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect vault --address <url> --token <token> [--namespace <ns>]',
    fields: [
      { name: 'address', label: 'Vault Address', required: true, secret: false, placeholder: 'https://vault.example.com:8200' },
      { name: 'token', label: 'Vault Token', required: true, secret: true, help: 'Use a periodic/renewable token scoped with a Kestrel policy.' },
      { name: 'namespace', label: 'Namespace', required: false, secret: false, help: 'Vault Enterprise / HCP namespace (optional).' },
    ],
    workflowExample: 'When a Vault secret has not been rotated in 90 days, rotate it and notify the security channel.',
  },
  {
    key: 'infisical',
    name: 'Infisical',
    category: 'Secrets',
    description: 'Manage Infisical secrets, folders, and syncs; react to audit events and approval requests.',
    href: '/integrations/infisical',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect infisical --client-id <id> --client-secret <secret> [--site-url <url>]',
    fields: [
      { name: 'client_id', label: 'Machine Identity Client ID', required: true, secret: false },
      { name: 'client_secret', label: 'Machine Identity Client Secret', required: true, secret: true },
      { name: 'site_url', label: 'Site URL', required: false, secret: false, placeholder: 'https://app.infisical.com' },
    ],
    workflowExample: 'When an Infisical secret changes in production, notify the team and trigger the affected secret syncs.',
  },
  // --- On-call ---
  {
    key: 'slack',
    name: 'Slack',
    category: 'On-call',
    description: 'Get incident alerts, approvals, and AI responses directly in Slack.',
    href: '/integrations/slack',
    connectionKind: 'oauth',
    cliCommand: 'kestrel integrations connect slack',
    docsNote: 'Slack app install — the CLI prints the OAuth URL to open in a browser.',
    popular: true,
    workflowExample: 'When any incident is detected, post a summary with suggested next steps to my Slack channel.',
  },
  {
    key: 'pagerduty',
    name: 'PagerDuty',
    category: 'On-call',
    description: 'Route incidents to PagerDuty and trigger workflows from PagerDuty events.',
    href: '/integrations/pagerduty',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect pagerduty --api-token <token> --webhook-secret <secret>',
    fields: [
      { name: 'api_token', label: 'REST API Token', required: true, secret: true },
      { name: 'webhook_secret', label: 'Webhook Signing Secret', required: true, secret: true },
    ],
    popular: true,
    workflowExample: 'When a PagerDuty incident is triggered, run an initial investigation and attach the findings as a note.',
  },
  // --- Project Management ---
  {
    key: 'jira',
    name: 'Jira',
    category: 'Project Management',
    description: 'Search Jira issues for incident context and create tickets from workflows.',
    href: '/integrations/knowledge-sources',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect jira --base-url <site-url> --email <email> --api-token <token>',
    fields: [
      { name: 'base_url', label: 'Site URL', required: true, secret: false, placeholder: 'https://your-site.atlassian.net' },
      { name: 'api_key', label: 'Account Email', required: true, secret: false },
      { name: 'api_token', label: 'Atlassian API Token', required: true, secret: true },
    ],
    workflowExample: 'When a production incident occurs, create a Jira ticket with the investigation summary.',
  },
  {
    key: 'linear',
    name: 'Linear',
    category: 'Project Management',
    description: 'Search Linear issues and create tickets from workflows.',
    href: '/integrations/knowledge-sources',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect linear --api-key <key>',
    fields: [
      { name: 'api_key', label: 'API Key', required: true, secret: true },
    ],
    workflowExample: 'When a deployment fails, create a Linear issue with the RCA and link the failing build.',
  },
  {
    key: 'confluence',
    name: 'Confluence',
    category: 'Project Management',
    description: 'Let Kestrel AI use your Confluence runbooks and docs.',
    href: '/integrations/knowledge-sources',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect confluence --base-url <site-url> --email <email> --api-token <token>',
    fields: [
      { name: 'base_url', label: 'Site URL', required: true, secret: false, placeholder: 'https://your-site.atlassian.net' },
      { name: 'api_key', label: 'Account Email', required: true, secret: false },
      { name: 'api_token', label: 'Atlassian API Token', required: true, secret: true },
    ],
    workflowExample: 'When an incident occurs, find the relevant runbook in Confluence and follow it to remediate.',
  },
  {
    key: 'notion',
    name: 'Notion',
    category: 'Project Management',
    description: 'Let Kestrel AI use your Notion pages and runbooks.',
    href: '/integrations/knowledge-sources',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect notion --api-token <token>',
    fields: [
      { name: 'api_token', label: 'Integration Token', required: true, secret: true },
    ],
    workflowExample: 'When an incident occurs, pull the relevant Notion runbook and use it to guide the response.',
  },
  {
    key: 'glean',
    name: 'Glean',
    category: 'Project Management',
    description: 'Search your company knowledge through Glean.',
    href: '/integrations/knowledge-sources',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect glean --api-key <key> [--base-url <url>]',
    fields: [
      { name: 'api_key', label: 'API Key', required: true, secret: true },
      { name: 'base_url', label: 'Instance URL', required: false, secret: false },
    ],
    workflowExample: 'When an incident occurs, search company knowledge via Glean for similar past incidents and their fixes.',
  },
  // --- Observability ---
  {
    key: 'datadog',
    name: 'Datadog',
    category: 'Observability',
    description: 'Query Datadog metrics and monitors from workflows on your clusters.',
    href: '/integrations/datadog',
    connectionKind: 'cluster',
    cliCommand: 'kestrel integrations connect kubernetes --cluster-name <name>',
    docsNote: 'Auto-detected: onboard a cluster with Datadog as its metrics source and it connects automatically.',
    workflowExample: 'When a workload is degraded, pull its Datadog metrics for the last hour and post an analysis to Slack.',
  },
  {
    key: 'posthog',
    name: 'PostHog',
    category: 'Observability',
    description: 'Monitor product analytics, feature flags, and error trends.',
    href: '/integrations/posthog',
    connectionKind: 'token',
    cliCommand: 'kestrel integrations connect posthog --api-key <key> --project-id <id>',
    fields: [
      { name: 'api_key', label: 'Personal API Key', required: true, secret: true },
      { name: 'project_id', label: 'Project ID', required: true, secret: false },
      { name: 'host', label: 'Host', required: false, secret: false, placeholder: 'https://us.posthog.com' },
    ],
    workflowExample: 'When PostHog error trends spike after a release, correlate with recent deployments and alert me.',
  },
];

export const INTEGRATION_CATEGORIES: IntegrationCategory[] = [
  'Infrastructure',
  'PaaS',
  'AI Compute',
  'Observability',
  'On-call',
  'Project Management',
  'Databases',
  'Secrets',
  'CI/CD',
  'IaC & GitOps',
  'Security',
];

export function getIntegration(key: string): IntegrationDef | undefined {
  return INTEGRATION_CATALOG.find((i) => i.key === key);
}

export function groupByCategory(defs: IntegrationDef[]): Map<IntegrationCategory, IntegrationDef[]> {
  const map = new Map<IntegrationCategory, IntegrationDef[]>();
  for (const cat of INTEGRATION_CATEGORIES) {
    const items = defs.filter((d) => d.category === cat);
    if (items.length > 0) map.set(cat, items);
  }
  return map;
}
